import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { MatterConversationsDocument } from '~/graphql/queries/conversation';

interface Props {
  matterId: string;
  skip?: boolean;
}

interface MatterUnreadCounts {
  /** Map from expertId → unread message count for that conversation. */
  byExpert: Record<string, number>;
  /** Total unread count across all conversations on the matter. */
  total: number;
  loading: boolean;
  hasError: boolean;
}

export const useMatterUnreadCounts = ({
  matterId,
  skip = false,
}: Props): MatterUnreadCounts => {
  const { data, loading, error } = useQuery(MatterConversationsDocument, {
    variables: { matterId },
    skip: skip || !matterId,
    fetchPolicy: 'cache-first',
  });

  const { byExpert, total } = useMemo(() => {
    const map: Record<string, number> = {};
    let sum = 0;

    for (const conversation of data?.matterConversations ?? []) {
      map[String(conversation.expertId)] = conversation.unreadCount;
      sum += conversation.unreadCount;
    }

    return { byExpert: map, total: sum };
  }, [data?.matterConversations]);

  return { byExpert, total, loading, hasError: Boolean(error) };
};
